import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { applyBoatFilters, type BoatFilters } from '@todo/core/hub-filters';
import type { Boat } from '@todo/core/boats';
import { fetchCityBoatCards } from '@todo/core/hub-pages';

/**
 * The /boat-rental/[city] page's filters. The page bakes the whole city, but
 * as text links: past the top cards its seed has no images or build years. So
 * the first time a filter or sort leaves its default, every boat in the city
 * is fetched once as a card (fetchCityBoatCards, ~250 rows at most); every
 * later choice filters and sorts those rows in the browser, with no request.
 * Nothing is fetched for visitors who never touch a filter, and the static
 * HTML — top cards + the full link list — is never affected.
 */
export function useCityBoatFilters(cityId: string, filters: BoatFilters, isDefault: boolean) {
  const [all, setAll] = useState<{ cityId: string; boats: Boat[] } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const request = useRef(0);

  const loaded = all?.cityId === cityId ? all.boats : null;
  const needed = !isDefault && loaded == null;

  useEffect(() => {
    if (!needed) return;
    const id = ++request.current;
    setLoading(true);
    setError(false);
    fetchCityBoatCards(cityId)
      .then((boats) => {
        if (id === request.current) setAll({ cityId, boats });
      })
      .catch(() => {
        if (id === request.current) setError(true);
      })
      .finally(() => {
        if (id === request.current) setLoading(false);
      });
    return () => {
      // A city change (client-side navigation) supersedes this request.
      request.current += 1;
      setLoading(false);
    };
  }, [needed, cityId, attempt]);

  const rows = useMemo(
    () => (isDefault || loaded == null ? null : applyBoatFilters(loaded, filters)),
    [isDefault, loaded, filters],
  );
  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return {
    /** The filtered + sorted boats, or null while unfiltered or not yet loaded. */
    rows,
    /** The city's boats are on their way (first filter only). */
    loading,
    /** Loading them failed; `retry` asks again. */
    error,
    retry,
  };
}
